import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from './ThemeContext';
import logoLight from './../Images/tsqs-hm-ft-logo1.gif';
import logoDark from './../Images/tsqs-hm-ft-logo.gif';
import { 
  FiMenu, FiX, FiInfo, FiPhone, FiLogIn, FiUserPlus,
  FiHome, FiTarget, FiBarChart2, FiUsers, FiSettings,
  FiStar, FiShield, FiSun, FiMoon
} from 'react-icons/fi';
// Because react-icons/fi sometimes gives issues depending on version, 
// using 'react-icons/fi' directly.
import { FiArrowRight } from 'react-icons/fi';
import './../Styles/LandingPage.css';

const LandingPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    // If the user is already logged in, redirect them to the dashboard
    const employeeId = localStorage.getItem('employeeId');
    if (employeeId) {
      navigate('/employee-projects', { replace: true });
    }
  }, [navigate]);

  // Handle clicking outside mobile menu
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isMenuOpen && !e.target.closest('.lp-mobile-menu') && !e.target.closest('.lp-hamburger')) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMenuOpen]);

  return (
    <div className={`lp-container ${theme === 'dark' ? 'lp-dark' : ''}`}>
      {/* HEADER */}
      <header className="lp-header">
        <div className="lp-logo-container">
          <>
            <img src={logoLight} alt="TSQS Logo" className="lp-logo logo-light" />
            <img src={logoDark} alt="TSQS Logo" className="lp-logo logo-dark" />
          </>
        </div>
        <div className="lp-title">
          <h1 className="lp-wordmark" aria-label="Tconnect">
            <span className="lp-wordmark-t">T</span>
            <span className="lp-wordmark-connect">connect</span>
          </h1>
        </div>
        <div className="lp-nav-controls">
          {/* THEME TOGGLE - Uncomment to re-enable dark mode toggle
          <button
            className="lp-theme-toggle"
            onClick={toggleTheme}
            aria-label="Toggle theme"
            title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          >
            {theme === 'dark' ? <FiSun /> : <FiMoon />}
          </button>
          */}

          <button 
            className="lp-hamburger" 
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle Menu"
          >
            {isMenuOpen ? <FiX /> : <FiMenu />}
          </button>

          {/* Dropdown Menu */}
          {isMenuOpen && (
            <div className="lp-mobile-menu">
              {/* <a href="#about" className="lp-menu-item" onClick={() => setIsMenuOpen(false)}>
                <FiInfo className="lp-menu-icon" /> About Us
              </a> */}
              {/* <a href="#contact" className="lp-menu-item" onClick={() => setIsMenuOpen(false)}>
                <FiPhone className="lp-menu-icon" /> Contact
              </a> */}
              {/* <div className="lp-menu-divider"></div> */}
              <button className="lp-menu-item" onClick={() => navigate('/login')}>
                <FiLogIn className="lp-menu-icon" /> Login
              </button>
              <button className="lp-menu-item" onClick={() => navigate('/register')}>
                <FiUserPlus className="lp-menu-icon" /> Register
              </button>
            </div>
          )}
        </div>
      </header>

      <main className="lp-main">
        {/* Decorative Background Dots */}
        <div className="lp-bg-dots lp-dots-right"></div>
        <div className="lp-bg-dots lp-dots-left"></div>

        {/* HERO SECTION */}
        <section className="lp-hero">
          <div className="lp-hero-content">
            <h1 className="lp-hero-title">
              Connect. Collaborate.<br/>
              Grow. One platform.<br/>
              <span className="lp-highlight">Every part of your organization.</span>
            </h1>
            <div className="lp-hero-line"></div>
            <p className="lp-hero-desc">
              Tconnect brings people, teams, services, and business operations together in one connected platform.
            </p>
            <div className="lp-hero-actions">
              <button className="lp-btn lp-btn-primary" onClick={() => navigate('/login')}>
                <FiLogIn /> Login
              </button>
              <button className="lp-btn lp-btn-outline" onClick={() => navigate('/register')}>
                <FiUserPlus /> Register
              </button>
            </div>
          </div>

          <div className="lp-hero-visual">
            {/* Mock Dashboard UI */}
            <div className="lp-mock-window">
              <div className="lp-mock-header">
                <span className="lp-dot"></span>
                <span className="lp-dot"></span>
                <span className="lp-dot"></span>
              </div>
              <div className="lp-mock-body">
                <div className="lp-mock-sidebar">
                  <div className="lp-mock-icon-active"><FiHome /></div>
                  <div className="lp-mock-icon"><FiTarget /></div>
                  <div className="lp-mock-icon"><FiBarChart2 /></div>
                  <div className="lp-mock-icon"><FiUsers /></div>
                  <div className="lp-mock-icon" style={{marginTop: 'auto'}}><FiSettings /></div>
                </div>
                <div className="lp-mock-content">
                  <div className="lp-mock-grid">
                    {/* Chart Card */}
                    <div className="lp-mock-card lp-card-large">
                      <h4>Performance Overview</h4>
                      <svg viewBox="0 0 400 150" className="lp-mock-chart">
                        <defs>
                          <linearGradient id="chartGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="rgba(249,115,22,0.4)" />
                            <stop offset="100%" stopColor="rgba(249,115,22,0)" />
                          </linearGradient>
                        </defs>
                        <path d="M0,130 L50,110 L100,70 L150,90 L200,60 L250,60 L300,10 L350,40 L400,20 L400,150 L0,150 Z" fill="url(#chartGrad)" />
                        <polyline points="0,130 50,110 100,70 150,90 200,60 250,60 300,10 350,40 400,20" fill="none" stroke="#EA580C" strokeWidth="3" />
                        <circle cx="50" cy="110" r="4" fill="#EA580C" />
                        <circle cx="100" cy="70" r="4" fill="#EA580C" />
                        <circle cx="150" cy="90" r="4" fill="#EA580C" />
                        <circle cx="200" cy="60" r="4" fill="#EA580C" />
                        <circle cx="250" cy="60" r="4" fill="#EA580C" />
                        <circle cx="300" cy="10" r="4" fill="#EA580C" />
                        <circle cx="350" cy="40" r="4" fill="#EA580C" />
                      </svg>
                    </div>

                    {/* Goals Card */}
                    <div className="lp-mock-card lp-card-small">
                      <h4>Goals</h4>
                      <div className="lp-mock-stat">
                        <FiTarget className="lp-stat-icon lp-text-orange" />
                        <div>
                          <span className="lp-stat-label lp-text-green">On Track</span>
                          <span className="lp-stat-value lp-text-green">76%</span>
                        </div>
                      </div>
                    </div>

                    {/* Reviews Card */}
                    <div className="lp-mock-card lp-card-small">
                      <h4>Reviews</h4>
                      <div className="lp-mock-stat">
                        <FiStar className="lp-stat-icon lp-text-orange" />
                        <div>
                          <span className="lp-stat-value">4.8</span>
                          <span className="lp-stat-label">Average Rating</span>
                        </div>
                      </div>
                    </div>

                    {/* Employees Card */}
                    <div className="lp-mock-card lp-card-small">
                      <h4>Employees</h4>
                      <div className="lp-mock-stat">
                        <FiUsers className="lp-stat-icon lp-text-orange" />
                        <div>
                          <span className="lp-stat-value">245</span>
                          <span className="lp-stat-label">Active Users</span>
                        </div>
                      </div>
                    </div>

                    {/* Donut Chart Card */}
                    <div className="lp-mock-card lp-card-small lp-donut-card">
                      <svg viewBox="0 0 100 100" className="lp-mock-donut">
                        <circle cx="50" cy="50" r="35" fill="none" stroke="#FDBA74" strokeWidth="20" strokeDasharray="160 60" strokeDashoffset="-20" />
                        <circle cx="50" cy="50" r="35" fill="none" stroke="#EA580C" strokeWidth="20" strokeDasharray="80 140" strokeDashoffset="20" />
                        <circle cx="50" cy="50" r="35" fill="none" stroke="#78350F" strokeWidth="20" strokeDasharray="40 180" strokeDashoffset="100" />
                      </svg>
                      <div className="lp-donut-legend">
                        <div className="lp-legend-item"><span className="lp-legend-dot lp-bg-orange"></span></div>
                        <div className="lp-legend-item"><span className="lp-legend-dot lp-bg-light"></span></div>
                        <div className="lp-legend-item"><span className="lp-legend-dot lp-bg-dark"></span></div>
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FEATURES SECTION */}
        <section className="lp-features">
          <div className="lp-feature-card">
            <div className="lp-feature-icon-wrapper">
              <FiUsers className="lp-feature-icon" />
            </div>
            <div className="lp-feature-text">
              <h3>Align Goals</h3>
              <p>Set clear goals and align them with organizational objectives.</p>
            </div>
          </div>
          
          <div className="lp-feature-card">
            <div className="lp-feature-icon-wrapper">
              <FiBarChart2 className="lp-feature-icon" />
            </div>
            <div className="lp-feature-text">
              <h3>Track Performance</h3>
              <p>Monitor progress in real-time with insightful analytics and reports.</p>
            </div>
          </div>
          
          <div className="lp-feature-card">
            <div className="lp-feature-icon-wrapper">
              <FiStar className="lp-feature-icon" />
            </div>
            <div className="lp-feature-text">
              <h3>Continuous Feedback</h3>
              <p>Encourage feedback and reviews to drive employee growth.</p>
            </div>
          </div>
          
          <div className="lp-feature-card">
            <div className="lp-feature-icon-wrapper">
              <FiShield className="lp-feature-icon" />
            </div>
            <div className="lp-feature-text">
              <h3>Role Based Access</h3>
              <p>Secure and customizable access for every role in your organization.</p>
            </div>
          </div>
        </section>
      </main>

      {/* FOOTER */}
      <footer className="lp-footer">
        <div className="lp-footer-curve">
          <svg viewBox="0 0 1440 100" preserveAspectRatio="none">
            <path d="M0,50 C320,120 420,-20 720,30 C1020,80 1120,-20 1440,50 L1440,100 L0,100 Z" fill="#EA580C" />
          </svg>
        </div>
        <div className="lp-footer-content">
          <p>&copy; {new Date().getFullYear()} Tconnect. All rights reserved.</p>
          <p className="lp-footer-powered">Powered by TSQS</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
